import React, { useState } from "react";
import { Calendar as CalendarIcon, ArrowRight, Phone, Mail } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { toast } from "sonner";

const presentationImg =
    "https://images.unsplash.com/photo-1755548788258-e4844461aaf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzb3JzJTIwd29ya3Nob3AlMjBwcmVzZW50YXRpb258ZW58MHx8fHwxNzc4MTE5ODkyfDA&ixlib=rb-4.1.0&q=85";

export default function Workshops() {
    const [date, setDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        institution: "",
        contact_name: "",
        email: "",
        phone: "",
        attendees: 25,
        location: "",
        notes: "",
    });

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!date) {
            toast.error("Please pick a preferred date");
            return;
        }
        try {
            setSubmitting(true);
            await api.post("/workshops/request", {
                ...form,
                attendees: Number(form.attendees),
                preferred_date: format(date, "yyyy-MM-dd"),
            });
            setSubmitted(true);
            toast.success("Request sent", { description: "We'll reply within one business day." });
        } catch (err) {
            toast.error("Could not submit", { description: err?.response?.data?.detail || err.message });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-background">
            {/* HERO */}
            <section className="border-b border-black/10">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 pt-16 pb-16 lg:pt-24">
                    <div className="lg:col-span-7">
                        <p className="overline">Hands-on workshops</p>
                        <h1 className="mt-3 font-heading font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                            We come to <span className="text-klein">your campus.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg text-muted-foreground leading-relaxed">
                            A focused, hands-on day with your faculty. Templates, live prompting, role-specific workflows,
                            and a defensible AI policy your committee can ship.
                        </p>
                        <ul className="mt-8 space-y-2 text-sm">
                            <li>– Half-day or full-day formats</li>
                            <li>– Tailored to your discipline mix</li>
                            <li>– Walk-away templates for every attendee</li>
                            <li>– Optional follow-up office hours</li>
                        </ul>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] w-full overflow-hidden bg-black">
                            <img src={presentationImg} alt="Professor presenting workshop" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FORM */}
            <section className="border-b border-black/10">
                <div className="container-edge py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <p className="overline">Schedule a session</p>
                        <h2 className="mt-3 font-heading font-black text-4xl tracking-tighter leading-[0.95]">
                            Tell us about your campus.
                        </h2>
                        <p className="mt-4 text-muted-foreground">
                            We'll confirm availability within one business day. Prefer to talk first?
                        </p>
                        <div className="mt-6 space-y-3">
                            <a href="tel:8135281102" className="flex items-center gap-2 font-medium hover:text-klein" data-testid="workshop-phone">
                                <Phone size={16} /> 813-528-1102
                            </a>
                            <a href="mailto:gdobson@degreesofsuccessinfo.com" className="flex items-center gap-2 font-medium hover:text-klein break-all" data-testid="workshop-email">
                                <Mail size={16} /> gdobson@degreesofsuccessinfo.com
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        {submitted ? (
                            <div data-testid="workshop-success" className="border border-black/15 p-10 bg-secondary">
                                <h3 className="font-heading font-bold text-2xl">Thanks — request received.</h3>
                                <p className="mt-3 text-muted-foreground">
                                    We've logged your workshop request. You'll get a reply at the email you provided
                                    within one business day.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="workshop-form"
                                className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-black/15 p-6 md:p-8 bg-white"
                            >
                                <Field label="Institution" required>
                                    <input
                                        data-testid="workshop-institution"
                                        required
                                        value={form.institution}
                                        onChange={onChange("institution")}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Contact name" required>
                                    <input
                                        data-testid="workshop-contact-name"
                                        required
                                        value={form.contact_name}
                                        onChange={onChange("contact_name")}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Email" required>
                                    <input
                                        data-testid="workshop-email-input"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={onChange("email")}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Phone">
                                    <input
                                        data-testid="workshop-phone-input"
                                        type="tel"
                                        value={form.phone}
                                        onChange={onChange("phone")}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Preferred date" required>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                data-testid="workshop-date-trigger"
                                                className={`${inputCls} text-left flex items-center justify-between`}
                                            >
                                                <span className={date ? "" : "text-muted-foreground"}>
                                                    {date ? format(date, "PPP") : "Pick a date"}
                                                </span>
                                                <CalendarIcon size={16} />
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 rounded-none" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={date}
                                                onSelect={setDate}
                                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </Field>
                                <Field label="Estimated attendees" required>
                                    <input
                                        data-testid="workshop-attendees"
                                        type="number"
                                        min={1}
                                        max={1000}
                                        required
                                        value={form.attendees}
                                        onChange={onChange("attendees")}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Campus / city" className="md:col-span-2">
                                    <input
                                        data-testid="workshop-location"
                                        value={form.location}
                                        onChange={onChange("location")}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="What do you want to cover?" className="md:col-span-2">
                                    <textarea
                                        data-testid="workshop-notes"
                                        rows={4}
                                        value={form.notes}
                                        onChange={onChange("notes")}
                                        className={inputCls}
                                    />
                                </Field>
                                <div className="md:col-span-2 flex justify-end">
                                    <button
                                        type="submit"
                                        data-testid="workshop-submit"
                                        disabled={submitting}
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50"
                                    >
                                        {submitting ? "Sending..." : "Request workshop"} <ArrowRight size={16} />
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

const inputCls =
    "w-full bg-white border border-black/20 px-3 py-2.5 text-sm focus:outline-none focus:border-klein focus:ring-2 focus:ring-klein/20";

function Field({ label, required, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-2 ${className}`}>
            <span className="text-xs uppercase tracking-widest font-semibold">
                {label}
                {required && <span className="text-klein"> *</span>}
            </span>
            {children}
        </label>
    );
}
